import { logger } from '@/helpers/log.service';
import { isNativeApp } from '@/helpers/platform-helper';
import get from 'lodash/get';

export abstract class BaseMobileService {
  // eslint-disable-next-line no-useless-constructor
  constructor(
    protected windowPath: string,
    protected serviceName: string,
    private callbacksFirst: boolean = false
  ) { }

  public isAvailable(): boolean {
    return isNativeApp() && !!get(window, this.windowPath);
  }

  protected getServiceInstance(): any {
    return get(window, this.windowPath);
  }

  protected demandServiceExists(): void {
    if (!this.isAvailable()) {
      throw new Error(`${this.serviceName} is not available`);
    }
  }

  protected demandServiceMethodExists(methodName: string): void {
    if (!(methodName in this.getServiceInstance())) {
      throw new Error(`Method "${methodName}" not found in ${this.serviceName}`);
    }
  }

  protected handleError(msg: string, err: any, rejectFn: Function) {
    logger.warn(`${msg} error:`, err);
    rejectFn(err);
  }

  protected handleResult(msg: string, result: any, resolveFn: Function) {
    logger.debug(`${msg} result:`, result);
    resolveFn(result);
  }

  protected execute(
    fn: string,
    modifyResultFn: Function | null = null,
    handleResultFn: Function | null = null,
    handleErrorFn: Function | null = null,
    ...args: any
  ): Promise<any> {
    const msg = `[${this.serviceName}:${fn}]`;
    logger.debug(`${msg} executing with args: `, ...args);

    return new Promise((resolve, reject) => {
      try {
        this.demandServiceExists();

        this.demandServiceMethodExists(fn);

        const onSuccess = handleResultFn
          ? (res) => handleResultFn(resolve, reject, res)
          : (res) => this.handleResult(msg, modifyResultFn ? modifyResultFn(res) : res, resolve);
        const onError = handleErrorFn
          ? (err) => handleErrorFn(resolve, reject, err)
          : (err) => this.handleError(msg, err, reject);

        if (this.callbacksFirst) {
          this.getServiceInstance()[fn](onSuccess, onError, ...args);
        } else {
          this.getServiceInstance()[fn](...args, onSuccess, onError);
        }
      } catch (error) {
        this.handleError(msg, error, reject);
      }
    });
  }
}
